import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import FlagIcon from '@mui/icons-material/Flag';
import BugReportIcon from '@mui/icons-material/BugReport';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useProjects } from '../context/ProjectContext';
import { dashboardApi } from '../api/acceptanceApi';
import { getApiErrorMessage } from '../api/client';
import { StatCard } from '../components/StatCard';
import { StatusBadge } from '../components/StatusBadge';
import { BugHistoryIcon } from '../components/BugHistoryIcon';
import { usePhaseTree, pickDefaultPhase } from './Acceptance/usePhaseTree';
import type { AcceptanceStatus } from '../types';
import type { DashboardStats } from '../types/acceptance';

type StatusFilter = 'ALL' | AcceptanceStatus;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: 'Tất cả trạng thái' },
  { value: 'PENDING', label: 'Chờ nghiệm thu' },
  { value: 'CONFIRMED', label: 'Đã xác nhận' },
  { value: 'CHANGE_REQUESTED', label: 'Yêu cầu thay đổi' },
];

/** Bảng điều khiển: tổng quan tiến độ nghiệm thu của dự án đang chọn. */
export function Dashboard(): JSX.Element {
  const { projects, selectedProjectId, setSelectedProjectId } = useProjects();
  const navigate = useNavigate();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [phaseFilter, setPhaseFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  const { phases, featuresByPhase, loadingPhases, loadingFeatures, errorByPhase, ensurePhaseLoaded } =
    usePhaseTree(selectedProjectId);

  useEffect(() => {
    setPhaseFilter('');
    setStatusFilter('ALL');
  }, [selectedProjectId]);

  // Chọn sẵn giai đoạn order_index cao nhất (hoà thì mới nhất) và tải tính
  // năng của riêng nó. Không còn tải toàn bộ giai đoạn của dự án.
  useEffect(() => {
    if (phaseFilter !== '' || loadingPhases || phases.length === 0) return;
    const target = pickDefaultPhase(phases);
    if (!target) return;
    setPhaseFilter(target.id);
    void ensurePhaseLoaded(target.id);
  }, [phaseFilter, loadingPhases, phases, ensurePhaseLoaded]);

  // Số liệu tổng quan chạy theo giai đoạn đang chọn. Chỉ gọi khi giai đoạn
  // thuộc đúng dự án hiện tại — lúc vừa đổi dự án, phaseFilter/phases còn là
  // của dự án cũ trong một nhịp render, gọi lúc đó chỉ phí một request.
  // Dự án chưa có giai đoạn nào thì lấy số liệu cả dự án (đều bằng 0).
  const statsPhaseId = useMemo(() => {
    if (!selectedProjectId || loadingPhases) return null;
    if (phases.length === 0) return '';
    const phase = phases.find((p) => p.id === phaseFilter);
    return phase && phase.project_id === selectedProjectId ? phase.id : null;
  }, [selectedProjectId, loadingPhases, phases, phaseFilter]);

  useEffect(() => {
    if (!selectedProjectId) {
      setStats(null);
      return;
    }
    if (statsPhaseId === null) return;
    let active = true;
    setStatsLoading(true);
    setStatsError(null);
    dashboardApi
      .get(selectedProjectId, statsPhaseId || undefined)
      .then((data) => {
        if (active) setStats(data);
      })
      .catch((err) => {
        if (active) setStatsError(getApiErrorMessage(err, 'Không thể tải số liệu tổng quan.'));
      })
      .finally(() => {
        if (active) setStatsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selectedProjectId, statsPhaseId]);

  const phaseNameById = useMemo(
    () => Object.fromEntries(phases.map((p) => [p.id, p.name])),
    [phases],
  );

  const filteredFeatures = useMemo(() => {
    if (!phaseFilter) return [];
    return (featuresByPhase[phaseFilter] ?? [])
      .filter((f) => statusFilter === 'ALL' || f.acceptance_status === statusFilter)
      .map((f) => ({ ...f, phaseId: phaseFilter }));
  }, [featuresByPhase, phaseFilter, statusFilter]);

  const featuresLoading = loadingPhases || loadingFeatures[phaseFilter] === true;
  const featuresError = phaseFilter ? errorByPhase[phaseFilter] : null;

  const progress = stats?.progress_percent ?? 0;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Bảng điều khiển
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="dashboard-project-label">Dự án</InputLabel>
              <Select
                labelId="dashboard-project-label"
                label="Dự án"
                value={selectedProjectId ?? ''}
                onChange={(e) => setSelectedProjectId(e.target.value || null)}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="dashboard-phase-label">Giai đoạn</InputLabel>
              <Select
                labelId="dashboard-phase-label"
                label="Giai đoạn"
                value={phaseFilter}
                onChange={(e) => {
                  const next = e.target.value;
                  setPhaseFilter(next);
                  void ensurePhaseLoaded(next);
                }}
              >
                {phases.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="dashboard-status-label">Trạng thái nghiệm thu</InputLabel>
              <Select
                labelId="dashboard-status-label"
                label="Trạng thái nghiệm thu"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {!selectedProjectId ? (
        <Alert severity="info">Chưa có dự án nào để hiển thị.</Alert>
      ) : statsError ? (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {statsError}
        </Alert>
      ) : (
        <>
          {/* columns={14}: 7 thẻ × md={2} vừa đúng một hàng (lưới 12 cột mặc
              định sẽ đẩy thẻ thứ 7 xuống dòng riêng). */}
          <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 14 }} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4} md={2}>
              <StatCard
                label="Tổng Feature"
                value={stats?.total_features ?? 0}
                icon={AssignmentIcon}
                color="info"
                loading={statsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <StatCard
                label="Đã xác nhận"
                value={stats?.confirmed ?? 0}
                icon={CheckCircleIcon}
                color="success"
                loading={statsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <StatCard
                label="Chờ duyệt"
                value={stats?.pending ?? 0}
                icon={HourglassEmptyIcon}
                color="default"
                loading={statsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <StatCard
                label="Yêu cầu thay đổi"
                value={stats?.change_requested ?? 0}
                icon={ReportProblemIcon}
                color="warning"
                loading={statsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <StatCard
                label="Bug đang mở"
                value={stats?.open_bugs ?? 0}
                icon={BugReportIcon}
                color="error"
                loading={statsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <StatCard
                label="Bug đã đóng"
                value={stats?.closed_bugs ?? 0}
                icon={DoneAllIcon}
                color="success"
                loading={statsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <StatCard
                label="CR đang mở"
                value={stats?.open_change_requests ?? 0}
                icon={FlagIcon}
                color="error"
                loading={statsLoading}
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Tiến độ nghiệm thu
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {progress}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, progress))}
              sx={{ height: 10, borderRadius: 5 }}
            />
          </Paper>

          <Paper variant="outlined">
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Danh sách tính năng
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tính năng</TableCell>
                    <TableCell>Giai đoạn</TableCell>
                    <TableCell>Trạng thái</TableCell>
                    <TableCell align="right">Hành động</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {featuresLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        Đang tải…
                      </TableCell>
                    </TableRow>
                  ) : featuresError ? (
                    // Tải hỏng phải nói là hỏng. Nếu để rơi vào nhánh "không có
                    // tính năng phù hợp bộ lọc" thì người dùng tưởng giai đoạn
                    // rỗng và không biết là cần thử lại.
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'error.main' }}>
                        {featuresError}
                      </TableCell>
                    </TableRow>
                  ) : filteredFeatures.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        Không có tính năng phù hợp bộ lọc.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredFeatures.map((f) => (
                      <TableRow key={f.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {f.name}
                            <BugHistoryIcon
                              totalBugCount={f.total_bug_count}
                              openBugCount={f.open_bug_count}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>{phaseNameById[f.phaseId] ?? '—'}</TableCell>
                        <TableCell>
                          <StatusBadge status={f.acceptance_status} />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() =>
                              navigate(
                                `/projects/${selectedProjectId}?phaseId=${f.phaseId}&featureId=${f.id}`,
                              )
                            }
                          >
                            Xem
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
